import { useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  useDepartments, useEmployees, useShiftLocations, useShifts, useTasks, useToggleTask,
} from '../api/hooks';
import type { Employee, Task } from '../api/types';
import { useT } from '../i18n/LangProvider';
import { Query, Pill } from '../ui/atoms';
import { Icon } from '../ui/Icon';
import { TaskModal, recurrenceLabel } from '../components/TaskModal';
import { TaskComposer } from '../components/TaskComposer';

const LOC_COLOR: Record<string, string> = {
  uff: '#e79aa6', vat: '#93cf8f', col: '#5bb8e6', nav: '#f2a63d', home: '#c3ace0', csam: '#9ad0c0', borgh: '#e6c34a',
};
const locColor = (k: string) => LOC_COLOR[k] ?? '#c9c9c9';
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const priorityTone: Record<string, string> = { alta: 'warn', media: 'info', bassa: 'neutral' };
const fmtDue = (v: string | null) => {
  if (!v) return null;
  const d = new Date(v);
  const hasTime = !(d.getHours() === 0 && d.getMinutes() === 0);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) + (hasTime ? ` · ${d.toTimeString().slice(0, 5)}` : '');
};

const SCOPES = [
  { id: 'mie', label: 'Le mie' },
  { id: 'ufficio', label: 'Per ufficio' },
  { id: 'reparto', label: 'Per reparto' },
  { id: 'tutte', label: 'Tutte' },
] as const;
type Scope = (typeof SCOPES)[number]['id'];

const STATUSES = [
  { id: 'aperte', label: 'Aperte' },
  { id: 'alta', label: 'Alta priorità' },
  { id: 'completate', label: 'Completate' },
  { id: 'tutte', label: 'Tutte' },
] as const;
type Status = (typeof STATUSES)[number]['id'];

const dayOf = (v: string | null) => v?.slice(0, 10) ?? null;

/**
 * Whether a task belongs to the day being looked at.
 *
 * An open task appears on its due day and stays put until it is done — running
 * late is still today's problem. A completed one shows on the day it was due or
 * ticked off. Undated tasks are a backlog and are always there.
 *
 * This is also what keeps the *next* occurrence of a recurring task out of the
 * way: ticking off today's opens tomorrow's, and tomorrow's belongs to tomorrow.
 */
function visibleOn(t: Task, day: string) {
  const due = dayOf(t.dueAt);
  if (!t.done) return !due || due <= day;
  return due === day || dayOf(t.doneAt) === day;
}

/** On a shared board every row belongs to the board, so the useful hint is when
 *  it is due and how often it comes back — not who it belongs to. */
function boardMeta(t: Task, tr: (s: string) => string) {
  const bits = [t.recurrence ? tr(recurrenceLabel(t.recurrence) ?? '') : null, fmtDue(t.dueAt)];
  return bits.filter(Boolean).join(' · ');
}

function TaskRow({ t, badge, onToggle, onOpen }: { t: Task; badge?: string; onToggle: () => void; onOpen: () => void }) {
  const tr = useT();
  const subs = t.subtasks ?? [];
  return (
    <div className="card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 12, opacity: t.done ? 0.6 : 1, cursor: 'pointer' }} onClick={onOpen}>
      <button className={`checkbox ${t.done ? 'is-on' : ''}`} onClick={(e) => { e.stopPropagation(); onToggle(); }} style={{ minWidth: 20, height: 20 }}>
        {t.done && <Icon name="check" size={11} stroke={3} />}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, textDecoration: t.done ? 'line-through' : 'none' }}>{t.title}</div>
        <div className="row" style={{ gap: 10, marginTop: 2, flexWrap: 'wrap' }}>
          {t.dueAt && <span className="muted" style={{ fontSize: 12 }}><Icon name="clock" size={11} /> {fmtDue(t.dueAt)}</span>}
          {badge && <span className="muted" style={{ fontSize: 12 }}><Icon name="user" size={11} /> {badge}</span>}
          {subs.length > 0 && <span className="muted" style={{ fontSize: 12 }}><Icon name="check" size={11} /> {subs.filter((s) => s.done).length}/{subs.length}</span>}
          {t.recurrence && <span className="muted" style={{ fontSize: 12 }}><Icon name="repeat" size={11} /> {tr(recurrenceLabel(t.recurrence) ?? '')}</span>}
        </div>
      </div>
      {t.tag && <Pill>{t.tag}</Pill>}
      <Pill tone={priorityTone[t.priority] ?? 'neutral'}>{tr(t.priority)}</Pill>
    </div>
  );
}

export function TasksScreen() {
  const tr = useT();
  const auth = useAuth();
  const q = useTasks();
  const employeesQ = useEmployees();
  const deptsQ = useDepartments();
  const officesQ = useShiftLocations();
  const shiftsQ = useShifts();
  const toggle = useToggleTask();

  const [scope, setScope] = useState<Scope>('mie');
  const [status, setStatus] = useState<Status>('aperte');
  const [day, setDay] = useState(() => iso(new Date()));
  const [openId, setOpenId] = useState<string | null>(null);

  const employees = employeesQ.data?.items ?? [];
  const departments = deptsQ.data ?? [];
  const offices = officesQ.data ?? [];
  const tasks = q.data?.items ?? [];
  const nameOf = (id: string | null) => employees.find((e) => e.id === id)?.name;

  /** Who works at each office on the selected day (from the shift plan). */
  const peopleByOffice = useMemo(() => {
    const map: Record<string, Employee[]> = {};
    for (const s of shiftsQ.data ?? []) {
      if (s.date.slice(0, 10) !== day) continue;
      const emp = employees.find((e) => e.id === s.employeeId);
      if (!emp) continue;
      const segs = (s.segments?.length ? s.segments : (s.locationKey ? [{ loc: s.locationKey }] : [])).filter((x) => x?.loc);
      for (const k of new Set(segs.map((x) => x!.loc as string))) (map[k] ??= []).push(emp);
    }
    return map;
  }, [shiftsQ.data, employees, day]);

  /** Everything the current day and status filters let through. */
  const visible = (list: Task[]) => {
    const onDay = list.filter((t) => visibleOn(t, day));
    if (status === 'aperte') return onDay.filter((t) => !t.done);
    if (status === 'alta') return onDay.filter((t) => !t.done && t.priority === 'alta');
    if (status === 'completate') return onDay.filter((t) => t.done);
    return onDay;
  };

  const mine = visible(tasks.filter((t) => t.assigneeId === auth.sub || (!t.assigneeId && !t.departmentId && !t.officeKey)));
  const all = visible(tasks);
  const isToday = day === iso(new Date());
  const shiftDay = (delta: number) => {
    const d = new Date(`${day}T00:00:00`);
    d.setDate(d.getDate() + delta);
    setDay(iso(d));
  };

  return (
    <div className="tasks-screen" style={{ padding: 24 }}>
      <div className="page-head" style={{ marginBottom: 14 }}>
        <div>
          <h1 className="h1">{tr('Task')}</h1>
          <div className="page-head__sub">{tr('Attività per persona, ufficio o reparto. I task di ufficio li vede chi è in turno lì.')}</div>
        </div>
        {/* One day at a time, in every view: what is on the board today should
            not change under you because tomorrow's copy already exists. */}
        <div className="tasks-day">
          <button className="btn btn--ghost btn--icon" aria-label={tr('Giorno precedente')} onClick={() => shiftDay(-1)}><Icon name="chevL" size={15} /></button>
          <input type="date" className="input tasks-day__input" value={day} onChange={(e) => setDay(e.target.value)} aria-label={tr('Giornata')} />
          <button className="btn btn--ghost btn--icon" aria-label={tr('Giorno successivo')} onClick={() => shiftDay(1)}><Icon name="chevR" size={15} /></button>
          <button className="btn btn--sm" onClick={() => setDay(iso(new Date()))} disabled={isToday}>{tr('Oggi')}</button>
        </div>
      </div>

      {!isToday && (
        <div className="tasks-otherday" role="status">
          <Icon name="calendar" size={13} />
          {tr('Stai guardando {d}, non oggi.', { d: new Date(`${day}T00:00:00`).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' }) })}
        </div>
      )}

      <TaskComposer offices={offices} departments={departments} employees={employees} meId={auth.sub ?? ''} />

      {/* scope + status */}
      <div className="tasks-filters">
        <div className="tasks-filter-group" aria-label={tr('Ambito dei task')}>
          {SCOPES.map((s) => (
            <button key={s.id} className={`task-filter ${scope === s.id ? 'is-active' : ''}`} onClick={() => setScope(s.id)}>{tr(s.label)}</button>
          ))}
        </div>
        <span className="tasks-filters__divider" />
        <div className="tasks-filter-group" aria-label={tr('Stato dei task')}>
          {STATUSES.map((s) => (
            <button key={s.id} className={`task-filter ${status === s.id ? 'is-active' : ''}`} onClick={() => setStatus(s.id)} style={{ fontSize: 12 }}>{tr(s.label)}</button>
          ))}
        </div>
      </div>

      <Query query={q} empty={{ test: (d) => d.items.length === 0, title: tr('Nessun task'), hint: tr('Aggiungi il primo task qui sopra.') }}>
        {() => (
          <>
            {scope === 'mie' && (
              <div className="col" style={{ gap: 8 }}>
                {mine.length === 0 && <div className="muted" style={{ fontSize: 13 }}>{tr('Nessun task in questa vista.')}</div>}
                {mine.map((t) => <TaskRow key={t.id} t={t} onToggle={() => toggle.mutate(t)} onOpen={() => setOpenId(t.id)} />)}
              </div>
            )}

            {scope === 'tutte' && (
              <div className="col" style={{ gap: 8 }}>
                {all.length === 0 && <div className="muted" style={{ fontSize: 13 }}>{tr('Nessun task in questa vista.')}</div>}
                {all.map((t) => (
                  <TaskRow key={t.id} t={t}
                    badge={t.officeKey ? (offices.find((o) => o.key === t.officeKey)?.short ?? t.officeKey)
                      : t.departmentId ? (departments.find((d) => d.id === t.departmentId)?.name ?? t.departmentId)
                      : nameOf(t.assigneeId)}
                    onToggle={() => toggle.mutate(t)} onOpen={() => setOpenId(t.id)} />
                ))}
              </div>
            )}

            {scope === 'ufficio' && (
              <div className="grid grid-2" style={{ gap: 16 }}>
                {offices.map((o) => {
                  const people = peopleByOffice[o.key] ?? [];
                  // Only what the office itself owns. Someone's personal task is
                  // theirs, not the office's, even while they are on shift here.
                  const list = visible(tasks.filter((t) => t.officeKey === o.key));
                  return (
                    <div key={o.key} className="card" style={{ padding: 16 }}>
                      <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ background: locColor(o.key), color: '#222', fontWeight: 700, fontSize: 12, borderRadius: 6, padding: '4px 9px' }}>{o.short ?? o.key.toUpperCase()}</span>
                        <div><strong>{o.label}</strong><div className="muted" style={{ fontSize: 12 }}>{tr('{a} in turno · {b} task', { a: people.length, b: list.length })}</div></div>
                      </div>
                      {people.length > 0 && (
                        <div className="row" style={{ gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
                          {people.map((p) => <span key={p.id} className="badge" style={{ fontSize: 11 }}>{p.name}</span>)}
                        </div>
                      )}
                      <div className="col" style={{ gap: 6 }}>
                        {list.length === 0 && <div className="muted" style={{ fontSize: 13 }}>{tr('Nessun task.')}</div>}
                        {list.map((t) => (
                          <div key={t.id} className="row" style={{ gap: 10, alignItems: 'center', padding: '6px 8px', borderRadius: 6, background: 'var(--bg-sunk,#f6f6f6)', cursor: 'pointer' }} onClick={() => setOpenId(t.id)}>
                            <button className={`checkbox ${t.done ? 'is-on' : ''}`} onClick={(e) => { e.stopPropagation(); toggle.mutate(t); }}>{t.done && <Icon name="check" size={10} stroke={3} />}</button>
                            <span style={{ flex: 1, fontSize: 13.5, textDecoration: t.done ? 'line-through' : 'none' }}>{t.title}</span>
                            <span className="muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{boardMeta(t, tr)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {scope === 'reparto' && (
              <div className="grid grid-2" style={{ gap: 16 }}>
                {departments.map((d) => {
                  const members = employees.filter((e) => e.departmentId === d.id);
                  // Department tasks only — the members' own to-dos stay private.
                  const list = visible(tasks.filter((t) => t.departmentId === d.id));
                  return (
                    <div key={d.id} className="card" style={{ padding: 16 }}>
                      <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 8 }}>
                        <span className={`pill pill--${d.color ?? 'neutral'}`} style={{ fontWeight: 700 }}>{d.short ?? '—'}</span>
                        <div><strong>{d.name}</strong><div className="muted" style={{ fontSize: 12 }}>{tr('{a} membri · {b} task', { a: members.length, b: list.length })}</div></div>
                      </div>
                      <div className="col" style={{ gap: 6 }}>
                        {list.length === 0 && <div className="muted" style={{ fontSize: 13 }}>{tr('Nessun task.')}</div>}
                        {list.map((t) => (
                          <div key={t.id} className="row" style={{ gap: 10, alignItems: 'center', padding: '6px 8px', borderRadius: 6, background: 'var(--bg-sunk,#f6f6f6)', cursor: 'pointer' }} onClick={() => setOpenId(t.id)}>
                            <button className={`checkbox ${t.done ? 'is-on' : ''}`} onClick={(e) => { e.stopPropagation(); toggle.mutate(t); }}>{t.done && <Icon name="check" size={10} stroke={3} />}</button>
                            <span style={{ flex: 1, fontSize: 13.5, textDecoration: t.done ? 'line-through' : 'none' }}>{t.title}</span>
                            <span className="muted" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{boardMeta(t, tr)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </Query>

      {openId && (() => { const t = tasks.find((x) => x.id === openId); return t ? <TaskModal task={t} employees={employees} onClose={() => setOpenId(null)} /> : null; })()}
    </div>
  );
}
